import { useFetchCaseStatuses } from "@/queries/useFetchCaseStatuses.ts";
import {
    Autocomplete,
    Checkbox,
    Chip,
    CircularProgress,
    Paper,
    TextField,
    Typography,
} from "@mui/material";
import { Check } from "lucide-react";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

type Props = {
    name: string;
    label: string;
    addNone?: boolean;
};

const SELECT_ALL_VALUE = "__SELECT_ALL__";

const LiabilitySelectMultiple = ({ name, label, addNone = false }: Props) => {
    const form = useFormContext();
    const { data, isError, isRefetching, isLoading } = useFetchCaseStatuses();

    const fieldValue = (form.watch(name) ?? []) as string[];

    const displayData = useMemo(() => {
        if (addNone) {
            return [
                {
                    idUUID: "none",
                    name: "None",
                },
                ...(data?.data ?? []),
            ];
        }
        return data?.data ?? [];
    }, [data?.data, addNone]);

    const getOptions = () => {
        return displayData.map((status) => ({
            value: status.idUUID ?? "",
            label: status.name ?? "",
        }));
    };

    const getSelectedOptions = () => {
        const options = getOptions();
        return options.filter((option) => fieldValue.includes(option.value));
    };

    const handleSelection = (values: string[]) => {
        form.setValue(name, values);
    };

    const options = getOptions();
    const allSelected = fieldValue.length > 0 && fieldValue.length === options.length;

    const selectAllOption = {
        value: SELECT_ALL_VALUE,
        label: allSelected ? "✓ Deselect All" : "Select All",
    };

    const optionsWithSelectAll = [selectAllOption, ...options];

    if (isError) {
        return (
            <Paper
                sx={{
                    p: 2,
                    borderRadius: "8px",
                }}
            >
                <Typography variant={"h5"}>Error loading liability statuses</Typography>
            </Paper>
        );
    }

    if (isLoading || isRefetching) {
        return (
            <Paper
                sx={{
                    p: 2,
                    borderRadius: "8px",
                }}
            >
                <CircularProgress size={"12px"} />
            </Paper>
        );
    }

    return (
        <Autocomplete
            multiple
            options={optionsWithSelectAll}
            value={getSelectedOptions()}
            onChange={(_event, newValue) => {
                const hasSelectAll = newValue.some((item) => item.value === SELECT_ALL_VALUE);

                if (hasSelectAll) {
                    if (allSelected) {
                        handleSelection([]);
                    } else {
                        handleSelection(options.map((opt) => opt.value));
                    }
                } else {
                    const normalValues = newValue
                        .filter((item) => item.value !== SELECT_ALL_VALUE)
                        .map((item) => item.value);
                    handleSelection(normalValues);
                }
            }}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderOption={(props, option, { selected }) => {
                const isSelectAll = option.value === SELECT_ALL_VALUE;
                const { key, ...otherProps } = props;

                return (
                    <li {...otherProps} key={option.value}>
                        <Checkbox
                            icon={
                                <span
                                    style={{
                                        width: 18,
                                        height: 18,
                                        border: "2px solid #ccc",
                                        borderRadius: 3,
                                    }}
                                />
                            }
                            checkedIcon={<Check size={18} />}
                            style={{ marginRight: 8 }}
                            checked={isSelectAll ? allSelected : selected}
                        />
                        <span style={{ fontWeight: isSelectAll ? 600 : 400 }}>{option.label}</span>
                    </li>
                );
            }}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                    <Chip
                        variant="outlined"
                        label={option.label}
                        {...getTagProps({ index })}
                        key={option.value}
                    />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={label}
                    error={!!form.formState.errors[name]}
                    helperText={form.formState.errors[name]?.message as string}
                />
            )}
            sx={{
                width: "100%",
                backgroundColor: "#fff",
                "& .MuiInputBase-root": {
                    minHeight: 56,
                },
            }}
        />
    );
};

export default LiabilitySelectMultiple;
